/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description Header Component.
 */

// Dependencies
import React from 'react';
import { Layout, Menu, Divider } from 'antd';
import { MenuOutlined, LogoutOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { Link, withRouter, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import {
  removeStorageItem,
  setStorageItem
} from 'commons/helpers/storage-helper';

// Assets
import { ReactComponent as BaxterLogo } from '@nephro/assets/baxter_logo_white.svg';

// Routes
import allRoutes from '../../routes';

// Styles
import styles from './styles.module.css';

// Dependencies Props
const { Sider } = Layout;
const { SubMenu, Item: MenuItem } = Menu;
let isPatient = false;

/**
 * Finds Complete Route Url
 * @param { String } pathname - current path.
 * @return { String | Boolean } - Returns a string if found a pathname, otherwise a empty string.
 */
const findCompleteUrl = (pathname) => {
  const completeUrl = allRoutes(isPatient)?.filter((item) =>
    item?.path?.split('/').includes(pathname)
  );
  return completeUrl[0];
};

/**
 * recursiveMenuItems Utility function.
 * @param { Object } route - Route Object.
 * @property { String } icon - Icon component.
 * @property { String } intlId - Intl Id.
 * @property { String } path - Path string.
 * @property { Object } children - Array of subroutes
 * @property { any } translate - Translation prop.
 */
const recursiveMenuItems = (
  { icon, url, list_childrens, description },
  translate
) =>
  Array.isArray(list_childrens) && list_childrens.length ? (
    <SubMenu
      // popupClassName={classes.subMenuPopup}
      key={url}
      title={
        <span>
          {icon !== undefined && icon}
          <span>{translate(findCompleteUrl(url)?.intlId || description)}</span>
        </span>
      }>
      {list_childrens.map((route) => recursiveMenuItems(route, translate))}
    </SubMenu>
  ) : (
    <MenuItem key={url} title={translate(description)}>
      <div className="w-full flex flex-row items-center">
        {icon !== undefined && findCompleteUrl(url)?.icon}
        <span className="text-sm ml-2 mb-0 overflow-hidden overflow-ellipsis whitespace-nowrap">
          {translate(findCompleteUrl(url)?.intlId || description)}
        </span>
        <Link to={findCompleteUrl(url)?.path} replace />
      </div>
    </MenuItem>
  );

/**
 * Finds Active Route
 * @param { Array } routes - current routes.
 * @param { String } pathname - current path.
 * @return { String | Boolean } - Returns a string if found a pathname, otherwise a false.
 */
const findActiveRoutePath = (routes, pathname) => {
  let activeRoute = null;
  // eslint-disable-next-line consistent-return
  const findRoute = ({ url, list_childrens }) => {
    if (Array.isArray(list_childrens) && list_childrens.length) {
      return findActiveRoutePath(list_childrens, pathname);
    }
    if (pathname?.split('/').includes(url)) return url;
  };

  for (const route of routes) {
    activeRoute = findRoute(route);
    if (activeRoute) {
      return activeRoute;
    }
  }

  return activeRoute;
};

// Text component
const titleBottomOptions = (text) => (
  <h2 className="text-sm mb-0 font-semibold ml-2 mt-1 text-grayLetters overflow-hidden overflow-ellipsis whitespace-nowrap">
    {text}
  </h2>
);

const SiderComponent = ({
  broken,
  onBreak,
  collapsed,
  onCollapse,
  visible,
  onShowDrawer,
  routes,
  signOut
}) => {
  const [activeRoutes, setActiveRoutes] = React.useState([]);
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const onLogout = async (useOkta) => {
    if (useOkta) {
      history.replace('/loginStaff');
    } else {
      history.replace('/');
    }
  };
  const handleLogout = async () => {
    const isPatientStorageValue = await getStorageItem('isPatient');
    const useOkta = !isPatientStorageValue;
    const user = await getStorageItem('user');
    await removeStorageItem('token');
    await removeStorageItem('okta-token-storage');
    setStorageItem('lastAuthPath', isPatientStorageValue ? '/' : '/loginStaff');
    const data = {
      identityProvider: isPatientStorageValue
        ? 'OKTA_PROVIDER'
        : 'LDAP_PROVIDER',
      userName: isPatientStorageValue
        ? user.identification_number
        : user.user_name
    };
    signOut(data, () => onLogout(useOkta));
    await caches.keys().then((keys) => {
      keys.forEach((key) => {
        return caches.delete(key);
      });
    });
  };
  React.useEffect(() => {
    const onSetActiveRoutes = async () => {
      const isPtnt = getStorageItem('isPatient');
      isPatient = isPtnt;
      setActiveRoutes([findActiveRoutePath(routes, location.pathname)]);
    };
    onSetActiveRoutes();
  }, [routes, location.pathname, broken]);

  return (
    <Sider
      breakpoint="lg"
      width={230}
      collapsedWidth={broken ? 0 : 48}
      onBreakpoint={onBreak}
      className={styles.sider}
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}>
      <Menu
        className={styles.menu}
        mode="inline"
        // defaultSelectedKeys={[`${siderOption[0].path}`]}
        selectedKeys={[...activeRoutes]}>
        {!broken && !visible && (
          <div
            className={`flex px-6 ${
              collapsed ? 'justify-center' : 'justify-end'
            }`}>
            <MenuOutlined
              className={styles.trigger}
              onClick={() => onCollapse(!collapsed)}
            />
          </div>
        )}
        {visible && (
          <div className={`flex flex-row flex-start items-center mb-4 ml-5`}>
            <MenuOutlined
              className={styles.triggerMobile}
              onClick={() => onShowDrawer(false)}
            />
            <BaxterLogo width={110} className="mt-1" />
          </div>
        )}
        {routes.map(
          (route) => route.description && recursiveMenuItems(route, translate)
        )}
        <div className={styles.logoutButton}>
          <Divider className={styles.dividerMenuStyle} />
          <div
            onClick={handleLogout}
            className={`w-full flex flex-row items-center mt-4 cursor-pointer z-0 ${
              !collapsed ? 'px-6' : 'justify-center'
            }`}>
            <LogoutOutlined className={styles.bottomOptionsIcon} />
            {!collapsed && titleBottomOptions(translate('dashboard.logout'))}
          </div>
        </div>
      </Menu>
      {/*  <div className="absolute bottom-0 w-full pb-8 bg-baxterGreenSider">
          <Divider className={styles.dividerMenuStyle} />
          <div
            onClick={async () => {
              await caches.keys().then((keys) => {
                keys.forEach((key) => {
                  return caches.delete(key);
                });
              });
              signOut(onLogout);
            }}
            className={`w-full flex flex-row items-center mt-4 cursor-pointer ${
              !collapsed ? 'px-6' : 'justify-center'
            }`}>
            <LogoutOutlined className={styles.bottomOptionsIcon} />
            {!collapsed && titleBottomOptions(translate('dashboard.logout'))}
          </div>
        </div> */}
    </Sider>
  );
};

/**
 * @name SiderComponent.propTypes
 * @type { propTypes } React PropTypes.
 * @property { Function } onToggleDrawer - Toggle drawer function.
 * @property { Boolean } collapsed - Is drawer collapsed boolean.
 * @property { Array } routes - Routes array.
 * @property { Object } location - Redux locaation object.
 * @property { any } intl - React-intl prop.
 */
SiderComponent.propTypes = {
  onBreak: PropTypes.func,
  onCollapse: PropTypes.func,
  collapsed: PropTypes.bool,
  onShowDrawer: PropTypes.func,
  visible: PropTypes.bool,
  signOut: PropTypes.func,
  broken: PropTypes.bool,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      path: PropTypes.string,
      isProtected: PropTypes.bool,
      component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
      intlId: PropTypes.string,
      icon: PropTypes.object,
      exact: PropTypes.bool
    })
  )
};

/**
 * Initialize default properties.
 */
SiderComponent.defaultProps = {
  collapsed: false,
  broken: false,
  visible: false,
  routes: []
};

export default withRouter(SiderComponent);
